import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { patchTask } from '../offline/optimistic';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useFaults, useHkMutation, useHkStaff, useHkTasks, useRooms } from '../api/queries';
import type { Fault, HkTask, HkTaskStatus, HkTaskType, Priority } from '../api/types';
import { formatDateTime, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

const TYPE_LABEL: Record<HkTaskType, string> = { CHECKOUT_CLEAN: 'Check-out clean', STAYOVER_CLEAN: 'Stay-over clean', DEEP_CLEAN: 'Deep clean' };
const STATUS: Record<HkTaskStatus, { label: string; tone: Tone }> = {
  OPEN: { label: 'To do', tone: 'warn' },
  IN_PROGRESS: { label: 'In progress', tone: 'info' },
  DONE: { label: 'Done, to inspect', tone: 'brand' },
  INSPECTED: { label: 'Inspected', tone: 'good' },
  CANCELLED: { label: 'Cancelled', tone: '' },
};
const PRIORITY: Record<Priority, { label: string; tone: Tone }> = { NORMAL: { label: 'Normal', tone: '' }, HIGH: { label: 'High', tone: 'warn' }, URGENT: { label: 'Urgent', tone: 'bad' } };

type Tab = 'mine' | 'all' | 'faults';

export function Housekeeping() {
  const { me, can } = useAuth();
  const manage = can('housekeeping.manage');
  const [tab, setTab] = useState<Tab>(manage ? 'all' : 'mine');
  const tasks = useHkTasks();
  const faults = useFaults();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [faultOpen, setFaultOpen] = useState<Fault | null>(null);
  const toast = useToast();
  const report = useReport();

  const generate = useHkMutation(() => api<{ created: number }>('/housekeeping/generate', { method: 'POST', body: {} }));

  const mine = (tasks.data ?? []).filter((t) => t.status !== 'INSPECTED' && (t.assignedTo === null || t.assignedTo.id === me?.id) && (t.status === 'OPEN' || t.status === 'IN_PROGRESS'));
  const all = tasks.data ?? [];
  const list = tab === 'mine' ? mine : all;
  const open = all.find((t) => t.id === openId) ?? null;

  return (
    <div className="page">
      <PageHead eyebrow="Housekeeping" title="Tasks">
        {manage ? (
          <div className="row">
            <Button
              loading={generate.isPending}
              onClick={() =>
                generate.mutate(undefined, {
                  onSuccess: (r) => toast.ok((r as { created: number }).created ? `Added ${(r as { created: number }).created} stay-over clean(s)` : 'Today\'s stay-over cleans are already set up'),
                  onError: report,
                })
              }
            >
              Stay-overs for today
            </Button>
            <Button variant="primary" onClick={() => setCreating(true)}>
              New task
            </Button>
          </div>
        ) : null}
      </PageHead>

      <div className="seg" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'mine'} onClick={() => setTab('mine')}>
          For me <span className="num muted">{mine.length}</span>
        </button>
        <button type="button" aria-pressed={tab === 'all'} onClick={() => setTab('all')}>
          All <span className="num muted">{all.length}</span>
        </button>
        <button type="button" aria-pressed={tab === 'faults'} onClick={() => setTab('faults')}>
          Faults <span className="num muted">{faults.data?.length ?? 0}</span>
        </button>
      </div>

      {tab === 'faults' ? (
        <>
          {can('maintenance.report') ? (
            <div>
              <Button onClick={() => setReporting(true)}>Report a fault</Button>
            </div>
          ) : null}
          {faults.isLoading ? (
            <Loading rows={2} />
          ) : faults.error ? (
            <ErrorNote error={faults.error} retry={() => void faults.refetch()} />
          ) : (faults.data ?? []).length === 0 ? (
            <div className="card flat">
              <Empty icon="check" title="No open faults" />
            </div>
          ) : (
            <div className="list">
              {faults.data!.map((f) => (
                <button key={f.id} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setFaultOpen(f)}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="title">{f.title}</span>
                    <Badge tone={PRIORITY[f.priority].tone}>{PRIORITY[f.priority].label}</Badge>
                  </div>
                  <span className="small muted">
                    {f.room ? `Room ${f.room.number}` : f.location} · {f.status === 'IN_PROGRESS' ? 'In progress' : 'Open'} · reported by {f.reportedBy}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : tasks.isLoading ? (
        <Loading rows={3} />
      ) : tasks.error ? (
        <ErrorNote error={tasks.error} retry={() => void tasks.refetch()} />
      ) : list.length === 0 ? (
        <div className="card flat">
          <Empty icon="check" title={tab === 'mine' ? 'Nothing waiting for you' : 'No cleaning tasks right now'}>
            {tab === 'mine' ? 'New jobs appear here when guests check out.' : 'Check-outs create a job automatically. Use "Stay-overs for today" for occupied rooms.'}
          </Empty>
        </div>
      ) : (
        <div className="list">
          {list.map((t) => {
            const done = t.checklist.filter((i) => i.done).length;
            return (
              <button key={t.id} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setOpenId(t.id)}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="title">
                    Room <span className="num">{t.room.number}</span> · {TYPE_LABEL[t.type]}
                  </span>
                  <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge>
                </div>
                <div className="row small muted">
                  {t.priority !== 'NORMAL' ? <Badge tone={PRIORITY[t.priority].tone}>{PRIORITY[t.priority].label}</Badge> : null}
                  <span>{t.room.roomType}</span>
                  <span>· {t.assignedTo ? t.assignedTo.name : 'Unassigned'}</span>
                  <span className="num">
                    · {done}/{t.checklist.length}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <TaskDialog task={open} onClose={() => setOpenId(null)} />
      <NewTaskDialog open={creating} onClose={() => setCreating(false)} />
      <ReportFaultDialog open={reporting} onClose={() => setReporting(false)} />
      <FaultDialog fault={faultOpen} onClose={() => setFaultOpen(null)} />
    </div>
  );
}

/* ------------------------------------------------------------------- task */
function TaskDialog({ task, onClose }: { task: HkTask | null; onClose: () => void }) {
  const { me, can } = useAuth();
  const manage = can('housekeeping.manage');
  const work = can('housekeeping.work');
  const toast = useToast();
  const report = useReport();
  const staff = useHkStaff(manage && !!task);
  const [reworkNote, setReworkNote] = useState('');
  const [reworking, setReworking] = useState(false);

  const qc = useQueryClient();
  // Starting, ticking and finishing a clean can be done with no signal (a housekeeper in a far corner); the change is
  // kept on the phone and sent later. Assigning, inspecting and cancelling are decisions that need a live answer.
  const queueFor = (v: { path: string; body?: Record<string, unknown> }) => {
    const t = task;
    if (!t) return {};
    const room = `room ${t.room.number}`;
    if (v.path === '/start') return { queue: { label: `Start cleaning ${room}`, optimistic: () => patchTask(qc, t.id, (x) => ({ ...x, status: 'IN_PROGRESS' as const, assignedTo: x.assignedTo ?? (me ? { id: me.id, name: me.name } : null) })) } };
    if (v.path === '/checklist') {
      const items = (v.body?.items ?? []) as { index: number; done: boolean }[];
      return { queue: { label: `Tick ${items.length === 1 ? 'an item' : 'items'} in ${room}`, optimistic: () => patchTask(qc, t.id, (x) => ({ ...x, checklist: x.checklist.map((c, i) => { const hit = items.find((u) => u.index === i); return hit ? { ...c, done: hit.done } : c; }) })) } };
    }
    if (v.path === '/complete') return { queue: { label: `Finish cleaning ${room}`, optimistic: () => patchTask(qc, t.id, (x) => ({ ...x, status: 'DONE' as const })) } };
    return {};
  };
  const act = useHkMutation((v: { path: string; method?: 'POST' | 'PUT' | 'PATCH'; body?: Record<string, unknown> }) => api(`/housekeeping/tasks/${task!.id}${v.path}`, { method: v.method ?? 'POST', body: v.body ?? {}, ...queueFor(v) }));
  const run = (path: string, done?: string, body?: Record<string, unknown>, method?: 'POST' | 'PUT' | 'PATCH') =>
    act.mutate(
      { path, ...(body ? { body } : {}), ...(method ? { method } : {}) },
      {
        onSuccess: () => {
          if (done) toast.ok(done);
        },
        onError: report,
      },
    );

  if (!task) return <Modal open={false} onClose={onClose} title="Task">{null}</Modal>;
  const mineOrFree = task.assignedTo === null || task.assignedTo.id === me?.id || manage;
  const editable = task.status === 'IN_PROGRESS' && work && mineOrFree;
  const allDone = task.checklist.every((i) => i.done);
  const finished = task.status === 'INSPECTED' || task.status === 'CANCELLED';

  return (
    <Modal open onClose={onClose} title={`Room ${task.room.number} · ${TYPE_LABEL[task.type]}`}>
      <div className="stack">
        <div className="row">
          <Badge tone={STATUS[task.status].tone}>{STATUS[task.status].label}</Badge>
          {task.priority !== 'NORMAL' ? <Badge tone={PRIORITY[task.priority].tone}>{PRIORITY[task.priority].label} priority</Badge> : null}
          <span className="small muted">
            {task.room.roomType} · {task.assignedTo ? task.assignedTo.name : 'Unassigned'}
          </span>
        </div>
        {task.notes ? <p className="small muted" style={{ whiteSpace: 'pre-line' }}>{task.notes}</p> : null}

        <ul className="checklist" aria-label="Checklist">
          {task.checklist.map((item, index) => (
            <li key={index}>
              <label>
                <input type="checkbox" checked={item.done} disabled={!editable || act.isPending} onChange={(e) => run('/checklist', undefined, { items: [{ index, done: e.target.checked }] }, 'PUT')} />
                <span>{item.text}</span>
              </label>
            </li>
          ))}
        </ul>
        {task.status === 'OPEN' ? <p className="hint">Start the task to tick off the checklist.</p> : null}

        {manage && !finished ? (
          <Field id="hk-assign" label="Assigned to">
            <select id="hk-assign" className="select" value={task.assignedTo?.id ?? ''} disabled={act.isPending} onChange={(e) => run('', 'Assignment updated', { assignedToId: e.target.value || null }, 'PATCH')}>
              <option value="">Unassigned</option>
              {(staff.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {reworking ? (
          <form
            className="stack"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (reworkNote.trim().length >= 3) {
                act.mutate(
                  { path: '/rework', body: { note: reworkNote.trim() } },
                  {
                    onSuccess: () => {
                      toast.ok('Sent back for rework');
                      setReworking(false);
                      setReworkNote('');
                    },
                    onError: report,
                  },
                );
              }
            }}
          >
            <Field id="hk-rework" label="What needs doing again?">
              <input id="hk-rework" className="input" value={reworkNote} onChange={(e) => setReworkNote(e.target.value)} autoFocus />
            </Field>
            <div className="modal-foot">
              <Button type="button" onClick={() => setReworking(false)}>
                Back
              </Button>
              <Button variant="danger" type="submit" disabled={reworkNote.trim().length < 3} loading={act.isPending}>
                Send back
              </Button>
            </div>
          </form>
        ) : (
          <div className="stack">
            {task.status === 'OPEN' && work && mineOrFree ? (
              <Button variant="primary" block loading={act.isPending} onClick={() => run('/start', 'Task started')}>
                Start
              </Button>
            ) : null}
            {task.status === 'IN_PROGRESS' && work && mineOrFree ? (
              <Button variant="primary" block disabled={!allDone} loading={act.isPending} onClick={() => run('/complete', task.room.status === 'VACANT_DIRTY' ? `Room ${task.room.number} is clean` : 'Task done')}>
                {allDone ? 'Mark done' : 'Tick every item to finish'}
              </Button>
            ) : null}
            {task.status === 'DONE' && manage ? (
              <div className="row">
                <Button variant="primary" loading={act.isPending} onClick={() => run('/inspect', `Room ${task.room.number} inspected`)}>
                  Inspected, all good
                </Button>
                <Button onClick={() => setReworking(true)}>Send back</Button>
              </div>
            ) : null}
            {manage && (task.status === 'OPEN' || task.status === 'IN_PROGRESS') ? (
              <Button variant="danger" block loading={act.isPending} onClick={() => run('/cancel', 'Task cancelled')}>
                Cancel task
              </Button>
            ) : null}
            {task.completedAt ? <p className="hint">Finished {formatDateTime(task.completedAt)}</p> : null}
            <Button block onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------- new task */
function NewTaskDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const rooms = useRooms();
  const staff = useHkStaff(open);
  const toast = useToast();
  const report = useReport();
  const [roomId, setRoomId] = useState('');
  const [type, setType] = useState<HkTaskType>('DEEP_CLEAN');
  const [priority, setPriority] = useState<Priority>('NORMAL');
  const [assignee, setAssignee] = useState('');
  const create = useHkMutation((body: Record<string, unknown>) => api('/housekeeping/tasks', { method: 'POST', body }));

  const eligible = (rooms.data ?? []).filter((r) => (type === 'STAYOVER_CLEAN' ? r.status === 'OCCUPIED' : r.status !== 'OCCUPIED'));
  return (
    <Modal open={open} onClose={onClose} title="New cleaning task">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!roomId) return;
          create.mutate(
            { roomId, type, priority, ...(assignee ? { assignedToId: assignee } : {}) },
            {
              onSuccess: () => {
                toast.ok('Task created');
                setRoomId('');
                onClose();
              },
              onError: report,
            },
          );
        }}
      >
        <Field id="nt-type" label="Kind of clean">
          <select
            id="nt-type"
            className="select"
            value={type}
            onChange={(e) => {
              setType(e.target.value as HkTaskType);
              setRoomId('');
            }}
          >
            <option value="DEEP_CLEAN">Deep clean (empty room)</option>
            <option value="CHECKOUT_CLEAN">Check-out clean (empty room)</option>
            <option value="STAYOVER_CLEAN">Stay-over clean (occupied room)</option>
          </select>
        </Field>
        <Field id="nt-room" label="Room">
          <select id="nt-room" className="select" value={roomId} onChange={(e) => setRoomId(e.target.value)} required>
            <option value="">Choose a room…</option>
            {eligible.map((r) => (
              <option key={r.id} value={r.id}>
                {r.number} · {r.roomType.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid-2">
          <Field id="nt-pri" label="Priority">
            <select id="nt-pri" className="select" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
            </select>
          </Field>
          <Field id="nt-who" label="Assign to">
            <select id="nt-who" className="select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Anyone</option>
              {(staff.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!roomId} loading={create.isPending}>
            Create task
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------------------------------------------------- faults */
function ReportFaultDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const rooms = useRooms();
  const toast = useToast();
  const report = useReport();
  const [roomId, setRoomId] = useState('');
  const [location, setLocation] = useState('');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState<Priority>('NORMAL');
  const create = useHkMutation((body: Record<string, unknown>) => api('/maintenance', { method: 'POST', body }));
  const valid = title.trim().length >= 3 && (roomId || location.trim().length >= 2);
  return (
    <Modal open={open} onClose={onClose} title="Report a fault">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          create.mutate(
            { title: title.trim(), priority, ...(roomId ? { roomId } : { location: location.trim() }), ...(details.trim() ? { details: details.trim() } : {}) },
            {
              onSuccess: () => {
                toast.ok('Fault reported');
                setTitle('');
                setDetails('');
                setRoomId('');
                setLocation('');
                onClose();
              },
              onError: report,
            },
          );
        }}
      >
        <Field id="rf-title" label="What is wrong?">
          <input id="rf-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Air conditioning not cooling" autoFocus />
        </Field>
        <Field id="rf-room" label="Room">
          <select id="rf-room" className="select" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            <option value="">Not a room</option>
            {(rooms.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.number} · {r.roomType.name}
              </option>
            ))}
          </select>
        </Field>
        {!roomId ? (
          <Field id="rf-loc" label="Where?" hint="e.g. Pool pump house, reception">
            <input id="rf-loc" className="input" value={location} onChange={(e) => setLocation(e.target.value)} />
          </Field>
        ) : null}
        <Field id="rf-pri" label="How urgent?">
          <select id="rf-pri" className="select" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            <option value="NORMAL">Normal</option>
            <option value="HIGH">High, a guest is affected</option>
            <option value="URGENT">Urgent, safety or no water or power</option>
          </select>
        </Field>
        <Field id="rf-det" label="More detail (optional)">
          <input id="rf-det" className="input" value={details} onChange={(e) => setDetails(e.target.value)} />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={create.isPending}>
            Report
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function FaultDialog({ fault, onClose }: { fault: Fault | null; onClose: () => void }) {
  const { can } = useAuth();
  const manage = can('maintenance.manage');
  const toast = useToast();
  const report = useReport();
  const staff = useHkStaff(manage && !!fault);
  const [resolution, setResolution] = useState('');
  const act = useHkMutation((v: { path: string; method?: 'POST' | 'PATCH'; body?: Record<string, unknown> }) => api(`/maintenance/${fault!.id}${v.path}`, { method: v.method ?? 'POST', body: v.body ?? {} }));
  if (!fault) return <Modal open={false} onClose={onClose} title="Fault">{null}</Modal>;
  const go = (path: string, done: string, body?: Record<string, unknown>, method?: 'POST' | 'PATCH') =>
    act.mutate(
      { path, ...(body ? { body } : {}), ...(method ? { method } : {}) },
      {
        onSuccess: () => {
          toast.ok(done);
          if (method !== 'PATCH') {
            setResolution('');
            onClose();
          }
        },
        onError: report,
      },
    );
  return (
    <Modal open onClose={onClose} title={fault.title}>
      <div className="stack">
        <div className="row">
          <Badge tone={PRIORITY[fault.priority].tone}>{PRIORITY[fault.priority].label}</Badge>
          <span className="small muted">
            {fault.room ? `Room ${fault.room.number}` : fault.location} · reported by {fault.reportedBy} · {formatDateTime(fault.createdAt)}
          </span>
        </div>
        {fault.details ? <p className="small">{fault.details}</p> : null}
        {manage ? (
          <>
            <Field id="ft-who" label="Assigned to">
              <select id="ft-who" className="select" value={fault.assignedTo?.id ?? ''} disabled={act.isPending} onChange={(e) => go('', 'Assignment updated', { assignedToId: e.target.value || null }, 'PATCH')}>
                <option value="">Unassigned</option>
                {(staff.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            {fault.status === 'OPEN' ? (
              <Button block loading={act.isPending} onClick={() => go('/start', 'Marked in progress')}>
                Start work
              </Button>
            ) : null}
            <Field id="ft-res" label="What was done?" hint="Needed to mark it resolved">
              <input id="ft-res" className="input" value={resolution} onChange={(e) => setResolution(e.target.value)} />
            </Field>
            <Button variant="primary" block disabled={resolution.trim().length < 3} loading={act.isPending} onClick={() => go('/resolve', 'Marked resolved', { resolution: resolution.trim() })}>
              Mark resolved
            </Button>
            <Button variant="danger" block loading={act.isPending} onClick={() => go('/cancel', 'Request cancelled')}>
              Cancel request
            </Button>
          </>
        ) : (
          <p className="small muted">{fault.assignedTo ? `${fault.assignedTo.name} is on it.` : 'Waiting for a manager to assign it.'}</p>
        )}
        <Button block onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
